import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiFilter,
  FiTrash2,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiAlertTriangle,
  FiEyeOff,
  FiFolder,
} from "react-icons/fi";
import {
  useGetAdminProjectsQuery,
  useDeleteAdminProjectMutation,
  useUpdateProjectVisibilityMutation,
} from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import { toast } from "react-toastify";

const AdminProjects = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    state: "",
    category: "",
    visible: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, error, refetch } = useGetAdminProjectsQuery({
    page,
    limit: 20,
    search,
    ...filters,
  });

  const [deleteProject, { isLoading: isDeleting }] =
    useDeleteAdminProjectMutation();
  const [updateVisibility] = useUpdateProjectVisibilityMutation();

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ state: "", category: "", visible: "" });
    setSearch("");
    setSearchInput("");
    setPage(1);
  };

  const handleDeleteClick = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProject) return;

    try {
      await deleteProject(selectedProject._id).unwrap();
      toast.success("Project deleted successfully");
      setShowDeleteModal(false);
      setSelectedProject(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete project");
    }
  };

  const handleToggleVisibility = async (project) => {
    try {
      await updateVisibility({
        id: project._id,
        visible: !project.visible,
      }).unwrap();
      toast.success(
        `Project ${project.visible ? "hidden" : "made visible"} successfully`
      );
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update visibility");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCreatorName = (project) => {
    const creator = project.creator;
    if (!creator) return "N/A";
    if (creator.fundername) return creator.fundername;
    return `${creator.firstname || ""} ${creator.lastname || ""}`.trim() || "N/A";
  };

  const projectStates = [
    "Draft",
    "Open Calls",
    "Closed Calls",
    "In Progress",
    "Completed",
    "Cancelled",
  ];

  const categories = [
    "Biology",
    "Chemistry",
    "Physics",
    "Medicine",
    "Computer Science",
    "Engineering",
    "Environmental Science",
    "Mathematics",
    "Other",
  ];

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Failed to load projects. Please try again.</p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Projects</h1>
          <p className="text-gray-500">
            Manage all platform projects ({data?.pagination?.total || 0} total)
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-light-primary transition-colors"
        >
          <FiRefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title or summary..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors ${
              showFilters || Object.values(filters).some(Boolean)
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <FiFilter size={16} />
            Filters
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                value={filters.state}
                onChange={(e) => handleFilterChange("state", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">All States</option>
                {projectStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <select
                value={filters.visible}
                onChange={(e) => handleFilterChange("visible", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">All</option>
                <option value="true">Visible</option>
                <option value="false">Hidden</option>
              </select>
            </div>

            <button
              onClick={clearFilters}
              className="text-sm text-primary hover:text-light-primary flex items-center gap-1"
            >
              <FiX size={14} />
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Bars height="40" width="40" color="#8538FF" ariaLabel="loading" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Project
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Creator
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      State
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Budget
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Raised
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Created
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Visible
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.projects?.map((project) => (
                    <tr key={project._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="min-w-0">
                          <p className="font-medium text-secondary truncate max-w-[250px]">
                            {project.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {project.category}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {project.creator?.images?.profile ? (
                              <img
                                src={project.creator.images.profile}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary text-xs font-semibold">
                                {getCreatorName(project)[0]?.toUpperCase() || "?"}
                              </span>
                            )}
                          </div>
                          <span className="text-gray-600 truncate max-w-[120px]">
                            {getCreatorName(project)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            project.state === "Open Calls"
                              ? "bg-green-100 text-green-700"
                              : project.state === "Draft"
                              ? "bg-gray-100 text-gray-700"
                              : project.state === "In Progress"
                              ? "bg-blue-100 text-blue-700"
                              : project.state === "Completed"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {project.state}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600">
                          {formatAmount(project.total_budget)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600">
                          {formatAmount(project.amount_raised)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(project.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleVisibility(project)}
                          className={`p-2 rounded-lg transition-colors ${
                            project.visible
                              ? "text-green-600 hover:bg-green-100"
                              : "text-gray-400 hover:bg-gray-100"
                          }`}
                          title={project.visible ? "Visible" : "Hidden"}
                        >
                          {project.visible ? (
                            <FiEye size={16} />
                          ) : (
                            <FiEyeOff size={16} />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/projects/${project._id}`}
                            className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <FiEye size={16} />
                          </Link>
                          <button
                            onClick={() => handleDeleteClick(project)}
                            className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Project"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.projects?.length === 0 && (
              <div className="text-center py-12">
                <FiFolder className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No projects found</p>
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing{" "}
                  {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{" "}
                  of {data.pagination.total} projects
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.pagination.pages, p + 1))
                    }
                    disabled={page === data.pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  Delete Project?
                </h3>
                <p className="text-sm text-gray-500 truncate max-w-[250px]">
                  {selectedProject?.title}
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              This action cannot be undone. The project and all associated data
              will be permanently deleted.
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedProject(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
